import React from "react";
import { Link } from "react-router-dom";
import { siteConfig, navigation } from "../../data/mockData";
import { Phone, Mail, MapPin, Clock, Linkedin, Instagram, Facebook } from "lucide-react";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.69a8.28 8.28 0 0 0 3.76.92V6.18a4.84 4.84 0 0 1-.76.05h-.24v.46z"/>
  </svg>
);

const FooterBrand = () => (
  <div className="lg:col-span-1">
    <div className="flex items-center gap-4 mb-4">
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex-shrink-0">
        <img
          src={siteConfig.kimHeadshot}
          alt="Kimberly Hess"
          className="w-full h-full object-cover"
        />
      </div>
      <p className="text-gray-400 text-sm leading-relaxed">
        Independent health and life insurance broker helping individuals, families, and small businesses find the right coverage.
      </p>
    </div>
    <div className="flex gap-3">
      {[
        { href: siteConfig.socialLinks.linkedin, icon: Linkedin, label: "LinkedIn" },
        { href: siteConfig.socialLinks.instagram, icon: Instagram, label: "Instagram" },
        { href: siteConfig.socialLinks.facebook, icon: Facebook, label: "Facebook" },
        { href: siteConfig.socialLinks.tiktok, icon: TikTokIcon, label: "TikTok" },
      ].map((social) => (
        <a
          key={social.label}
          href={social.href}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-[#3A7D44] transition-colors duration-200 cursor-pointer"
          aria-label={social.label}
          title={social.label}
        >
          <social.icon size={18} />
        </a>
      ))}
    </div>
  </div>
);

const FooterQuickLinks = () => (
  <div>
    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">Quick Links</h3>
    <ul className="space-y-3">
      {navigation.map((item) => (
        <li key={item.path}>
          <Link to={item.path} className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
            {item.label}
          </Link>
        </li>
      ))}
      {["Book a Call", "Get a Quote"].map((label) => (
        <li key={label}>
          <a href={siteConfig.calendlyUrl} className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
            {label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const FooterServices = () => {
  const serviceLinks = [
    "Health Insurance", "Life Insurance", "Dental Coverage",
    "Vision Coverage", "Small Business Plans", "Supplemental Plans",
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">Services</h3>
      <ul className="space-y-3">
        {serviceLinks.map((label) => (
          <li key={label}>
            <Link to="/services" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const FooterContact = () => {
  const contactItems = [
    { icon: Phone, content: siteConfig.phoneFormatted, href: `tel:${siteConfig.phone}` },
    { icon: Mail, content: siteConfig.email, href: `mailto:${siteConfig.email}` },
    { icon: MapPin, content: `${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}` },
    { icon: Clock, content: siteConfig.hours },
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">Contact</h3>
      <ul className="space-y-4">
        {contactItems.map((item) => (
          <li key={item.content} className="flex items-start gap-3">
            <item.icon size={16} className="text-[#3A7D44] mt-0.5 flex-shrink-0" />
            {item.href ? (
              <a href={item.href} className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                {item.content}
              </a>
            ) : (
              <span className="text-gray-400 text-sm">{item.content}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#1B2A1B] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <FooterBrand />
          <FooterQuickLinks />
          <FooterServices />
          <FooterContact />
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs">
              &copy; {new Date().getFullYear()} Hess Insurance Solutions. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs">
              Licensed Health & Life Insurance Agent in All 50 States
            </p>
            <div className="flex gap-4 text-xs">
              {["Terms & Conditions", "Privacy Policy", "Accessibility"].map((label) => (
                <span key={label} className="text-gray-500 hover:text-gray-400 cursor-pointer transition-colors">
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
